import React, { MutableRefObject, useRef, useState } from 'react';
import { ManagedFarmFormWrapper } from '../ManagedFarmFormWrapper';
import { managedFarmFormSteps } from '../../../../constants/managedFarm';
import { useNavigate } from 'react-router-dom';
import { managedFarmPageInfo } from '../../../../constants/pageInfo/pageInfo.constant';
import { SurveyForm } from '../../../../components/common/survey/form/SurveyForm';
import { SurveyInputHeader } from '../../../../components/common/survey/inputs/SurveyInputHeader';
import styled from 'styled-components';
import { PopupConfirmModal } from '../../../../components/common/modal/popup/PopupConfirmModal';
import { useAppDispatch, useAppSelector } from '../../../../hooks/store/useStore';
import { managedFarmSlice } from '../../../../store/managedFarmSlice';
import { BasicInputWithExtras } from '../../../../components/common/inputs/BasicInputWithExtras';
import { InputValue } from '../../../../interfaces/Element.interfaces';
import { Helmet } from 'react-helmet';
import { ampli } from '../../../../ampli';

const BOTTOM_FIXED_HEIGHT = 72;
const BOTTOM_BLURRED_SECTION_HEIGHT = 40;

const taskTypeList: string[] = ['수확', '방제', '조사료', '베일러', '래핑', '집게', '추비', '파종', '경운', '기타'];
const equipmentList: string[] = ['콤바인', '트랙터', '드론', '이앙기', '기타'];
const cropTypeList: string[] = [
  '벼',
  '보리',
  '콩',
  '양파',
  '배추',
  '파',
  '옥수수',
  '밀',
  '감자',
  '고구마',
  '과수',
  '나이그라스',
  '라이그래스',
  '수단그라스',
  '기타',
];

const ERROR_MESSAGES = {
  taskType: {
    title: '작업 종류를 선택해주세요',
    buttonName: '작업 종류 선택하기',
  },
  taskTypeEtc: {
    title: '작업 종류를 입력해주세요.',
    buttonName: '작업 종류 입력하기',
  },
  equipment: {
    title: '사용 장비를 선택해주세요',
    buttonName: '사용 장비 선택하기',
  },
  equipmentEtc: {
    title: '사용 장비를 입력해주세요.',
    buttonName: '사용 장비 입력하기',
  },
  cropType: {
    title: '작물 종류를 선택해주세요',
    buttonName: '작물 종류 선택하기',
  },
  cropTypeEtc: {
    title: '작물 종류를 입력해주세요.',
    buttonName: '작물 종류 입력하기',
  },
} as const;

export const ManagedFarmFormTaskTypePage = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const managedFarmSelector = useAppSelector((state) => state.managedFarm);
  const managedFarmActions = managedFarmSlice.actions;

  const [taskType, setTaskType] = useState<string>(managedFarmSelector.taskType);
  const [taskTypeEtc, setTaskTypeEtc] = useState<string>(managedFarmSelector.taskTypeEtc);
  const [equipment, setEquipment] = useState<string>(managedFarmSelector.equipment);
  const [equipmentEtc, setEquipmentEtc] = useState<string>(managedFarmSelector.equipmentEtc);
  const [cropType, setCropType] = useState<string>(managedFarmSelector.cropType);
  const [cropTypeEtc, setCropTypeEtc] = useState<string>(managedFarmSelector.cropTypeEtc);

  const taskTypeSectionRef = useRef<HTMLDivElement | null>(null);
  const taskTypeEtcInputRef = useRef<HTMLInputElement | null>(null);
  const equipmentSectionRef = useRef<HTMLDivElement | null>(null);
  const equipmentEtcInputRef = useRef<HTMLInputElement | null>(null);
  const cropTypeSectionRef = useRef<HTMLDivElement | null>(null);
  const cropTypeEtcInputRef = useRef<HTMLInputElement | null>(null);

  const [inputError, setInputError] = useState<{ title: string; buttonName: string; errorHandler?: () => void }>();
  const [isShowErrorModal, setIsShowErrorModal] = useState<boolean>(false);

  const currentStep = managedFarmFormSteps.STEP2;
  const steps = Object.values(managedFarmFormSteps);

  const scrollToSectionBottom = (ref: MutableRefObject<HTMLDivElement | null>) => {
    setTimeout(() => {
      if (ref && ref.current) {
        const elementBottom = ref.current.getBoundingClientRect().bottom;
        const currentScroll = window.scrollY || window.pageYOffset;
        const scrollTo =
          currentScroll + elementBottom - window.innerHeight + BOTTOM_FIXED_HEIGHT + BOTTOM_BLURRED_SECTION_HEIGHT;
        window.scrollTo({ behavior: 'smooth', top: scrollTo });
      }
    }, 50);
  };

  const focusInput = (ref: MutableRefObject<HTMLInputElement | null>) => {
    setTimeout(() => {
      if (ref && ref.current) {
        ref.current.focus();
      }
    }, 50);
  };

  const handleButtonClick = (dataType: 'taskType' | 'equipment' | 'cropType', value: string) => {
    if (dataType === 'taskType') {
      setTaskType(value);
      if (value === '기타') {
        focusInput(taskTypeEtcInputRef);
      } else {
        scrollToSectionBottom(equipmentSectionRef);
      }
      return;
    }

    if (dataType === 'equipment') {
      setEquipment(value);
      if (value === '기타') {
        focusInput(equipmentEtcInputRef);
      } else {
        scrollToSectionBottom(cropTypeSectionRef);
      }
      return;
    }

    if (dataType === 'cropType') {
      setCropType(value);
      if (value === '기타') {
        focusInput(cropTypeEtcInputRef);
      } else {
        scrollToSectionBottom(cropTypeEtcInputRef);
      }
    }
  };

  const checkHasInputError = (): boolean => {
    if (!taskType) {
      setInputError(ERROR_MESSAGES.taskType);
      setIsShowErrorModal(true);
      return true;
    }

    if (taskType === '기타' && !taskTypeEtc) {
      setInputError({ ...ERROR_MESSAGES.taskTypeEtc, errorHandler: () => focusInput(taskTypeEtcInputRef) });
      setIsShowErrorModal(true);
      return true;
    }

    if (!equipment) {
      setInputError(ERROR_MESSAGES.equipment);
      setIsShowErrorModal(true);
      return true;
    }

    if (equipment === '기타' && !equipmentEtc) {
      setInputError({ ...ERROR_MESSAGES.equipmentEtc, errorHandler: () => focusInput(equipmentEtcInputRef) });
      setIsShowErrorModal(true);
      return true;
    }

    if (!cropType) {
      setInputError(ERROR_MESSAGES.cropType);
      setIsShowErrorModal(true);
      return true;
    }

    if (cropType === '기타' && !cropTypeEtc) {
      setInputError({ ...ERROR_MESSAGES.cropTypeEtc, errorHandler: () => focusInput(cropTypeEtcInputRef) });
      setIsShowErrorModal(true);
      return true;
    }

    return false;
  };

  const isEtcTaskTypeSelected = taskType === '기타';
  const isEtcEquipmentSelected = equipment === '기타';
  const isEtcCropTypeSelected = cropType === '기타';

  const dispatchTaskInfo = () => {
    if (!taskType || !equipment || !cropType) {
      return;
    }

    dispatch(managedFarmActions.changeTaskType(taskType));
    dispatch(managedFarmActions.changeEquipment(equipment));
    dispatch(managedFarmActions.changeCropType(cropType));

    if (isEtcTaskTypeSelected) {
      dispatch(managedFarmActions.changeTaskTypeEtc(taskTypeEtc));
    }

    if (isEtcEquipmentSelected) {
      dispatch(managedFarmActions.changeEquipmentEtc(equipmentEtc));
    }

    if (isEtcCropTypeSelected) {
      dispatch(managedFarmActions.changeCropTypeEtc(cropTypeEtc));
    }
  };

  return (
    <>
      <Helmet>
        <title>{managedFarmPageInfo.formTaskTypePage.title}</title>
      </Helmet>
      <ManagedFarmFormWrapper
        headerTitle={'작물 종류를 선택해주세요'}
        canConfirm
        steps={steps}
        currentStep={currentStep}
        confirmHandler={() => {
          const isInputErrorExist = checkHasInputError();
          if (isInputErrorExist) {
            return;
          }
          dispatchTaskInfo();
          ampli.pvmGoNextPageManagedFarmTaskInfo({
            task_type: taskType !== '기타' ? taskType : taskTypeEtc,
            equipment: equipment !== '기타' ? equipment : equipmentEtc,
            crop_type: cropType !== '기타' ? cropType : cropTypeEtc,
          });
          navigate(managedFarmPageInfo.formTaskSchedulePage.path, { replace: true });
        }}
        prevButtonHandler={() => {
          dispatchTaskInfo();
          navigate(managedFarmPageInfo.formFieldOwnerPage.path, { replace: true });
        }}
      >
        <SurveyForm>
          <SurveyInputSection>
            <InputContainer ref={taskTypeSectionRef}>
              <SurveyInputHeader inputTitle={'작업 종류'} isRequired />
              <ButtonContainer>
                <RowContainer column={4}>
                  {taskTypeList.map((task) => (
                    <SelectButton
                      key={task}
                      isActive={task === taskType}
                      onClick={() => handleButtonClick('taskType', task)}
                    >
                      <ButtonText isActive={task === taskType}>{task}</ButtonText>
                    </SelectButton>
                  ))}
                </RowContainer>
              </ButtonContainer>
              {isEtcTaskTypeSelected && (
                <BasicInputWithExtras
                  type={'string'}
                  inputValue={taskTypeEtc}
                  placeholder={'작업 종류를 입력해주세요.'}
                  onChange={(value: InputValue) => setTaskTypeEtc(value as string)}
                  ref={taskTypeEtcInputRef}
                />
              )}
            </InputContainer>

            {taskType && (
              <InputContainer ref={equipmentSectionRef}>
                <SurveyInputHeader inputTitle={'사용 장비'} isRequired />
                <ButtonContainer>
                  <RowContainer column={3}>
                    {equipmentList.map((equipmentItem) => (
                      <SelectButton
                        key={equipmentItem}
                        isActive={equipmentItem === equipment}
                        onClick={() => handleButtonClick('equipment', equipmentItem)}
                      >
                        <ButtonText isActive={equipmentItem === equipment}>{equipmentItem}</ButtonText>
                      </SelectButton>
                    ))}
                  </RowContainer>
                </ButtonContainer>
                {isEtcEquipmentSelected && (
                  <BasicInputWithExtras
                    type={'string'}
                    inputValue={equipmentEtc}
                    placeholder={'사용 장비를 입력해주세요.'}
                    onChange={(value: InputValue) => setEquipmentEtc(value as string)}
                    ref={equipmentEtcInputRef}
                  />
                )}
              </InputContainer>
            )}

            {taskType && equipment && (
              <InputContainer ref={cropTypeSectionRef}>
                <SurveyInputHeader inputTitle={'작물 종류'} isRequired />
                <ButtonContainer>
                  <RowContainer column={4}>
                    {cropTypeList.map((crop) => (
                      <SelectButton
                        key={crop}
                        isActive={crop === cropType}
                        onClick={() => handleButtonClick('cropType', crop)}
                      >
                        <ButtonText isActive={crop === cropType}>{crop}</ButtonText>
                      </SelectButton>
                    ))}
                  </RowContainer>
                </ButtonContainer>
                {isEtcCropTypeSelected && (
                  <BasicInputWithExtras
                    type={'string'}
                    inputValue={cropTypeEtc}
                    placeholder={'작물 종류를 입력해주세요.'}
                    onChange={(value: InputValue) => setCropTypeEtc(value as string)}
                    ref={cropTypeEtcInputRef}
                  />
                )}
              </InputContainer>
            )}
          </SurveyInputSection>
        </SurveyForm>
      </ManagedFarmFormWrapper>

      <PopupConfirmModal
        isShow={isShowErrorModal}
        hasCloseButton={false}
        closeModal={() => setIsShowErrorModal(false)}
        title={inputError?.title ?? ''}
        buttonArray={[
          {
            text: inputError?.buttonName ?? '',
            onClick: () => {
              setIsShowErrorModal(false);
              inputError?.errorHandler?.();
            },
            color: 'blue',
          },
        ]}
      />
    </>
  );
};

const SurveyInputSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  flex: 1 0 0;
  align-self: stretch;

  padding: 0px 16px;
`;

const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`;

const ButtonContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`;

const RowContainer = styled.div<{ column: number }>`
  width: 100%;

  display: grid;
  gap: 8px;
  align-self: stretch;
  grid-template-columns: ${(props) => props.column && `repeat(${props.column}, 1fr)`};
`;

const SelectButton = styled.button<{ isActive: boolean }>`
  height: 72px;

  display: flex;
  justify-content: space-between;
  align-items: center;
  flex: 1 0 0;
  align-self: stretch;

  border-radius: var(--radius-medium, 8px);
  ${(props) => props.isActive && 'border: 1px solid var(--Light-Scheme-Color-primary-blue, #196eff);'}
  background: ${(props) => (props.isActive ? '#e5e9ff' : '#F2F2F7')};
`;

const ButtonText = styled.span<{ isActive: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex: 1 0 0;

  color: ${(props) => (props.isActive ? '#196EFF' : '#505363')};

  /* System/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 85%; /* 11.9px */

  white-space: nowrap;
`;
